import { ApiError } from '../core/api/http'
import { readLocalJson, writeLocalJson } from '../core/storage'
import { daysBetween } from '../core/format/dates'
import { boxContribution, orderContribution, packagingFor, paymentCostCents } from '../core/rules/margin'
import { localCatalog } from '../data/catalog'
import { defaultCostModel } from '../data/costDefaults'
import { policy } from '../data/policies'
import { db } from './db'
import { reply } from './reply'
import { invoiceCosts, normalizeName } from './seed/invoiceCosts'
import type { AdminBackend } from '../core/types/backend'
import type { CostModel, CostsResponse, MarginReport, MarginStream } from '../core/types/margin'

/**
 * DEMO — kosten en marge aan de kant van de server (LARAVEL.md §9.8). Het kostenmodel staat in de
 * browser; inkoopprijzen komen uit de voorraad (gemiddelde inkoop), anders uit de Medik8-factuur.
 * Het margeoverzicht rekent met vaste voorbeeldaantallen per stroom, maar met Sarah's echte kosten:
 * pas je in Instellingen iets aan, dan zie je het hier terug.
 */

const KEY = 'demo-costs'
type Stored = { revision: number; costs: CostModel }

const load = (): Stored => {
  const s = readLocalJson<Stored | null>(KEY, null)
  return s ? { revision: s.revision, costs: { ...defaultCostModel, ...s.costs } } : { revision: 1, costs: defaultCostModel }
}

/** Gemiddelde inkoop per artikel: de voorraad wint, anders de factuur. */
function itemCosts(): Record<string, number> {
  const out: Record<string, number> = {}
  const rows = new Map(db.stockItems().map((r) => [r.id, r.costPriceCents]))
  for (const p of localCatalog.products) {
    if (p.kind === 'giftcard' || p.kind === 'kuur') continue
    const fromStock = rows.get(p.id)
    const fromInvoice = invoiceCosts.find((c) => c.name === normalizeName(p.name))?.cents
    const cents = fromStock ?? fromInvoice
    if (cents !== undefined) out[p.id] = cents
  }
  return out
}

const response = (): CostsResponse => ({ ...load(), itemCosts: itemCosts() })

const validCents = (n: unknown) => typeof n === 'number' && Number.isInteger(n) && n >= 0 && n <= 100_000

function costsProblem(c: CostModel): string | null {
  const fees = Object.values(c.payment)
  if (fees.some((f) => !validCents(f.fixedCents) || typeof f.percent !== 'number' || f.percent < 0 || f.percent > 20)) return 'Betaalkosten: vul een bedrag en een percentage tussen 0 en 20 in.'
  if (![...Object.values(c.shipping), c.packaging.pakket, c.packaging.brievenbus, c.targetBoxLeftCents, c.subscriptionGiftCents].every(validCents))
    return 'Vul bij verzenden, verpakking en het abonnement geldige bedragen in.'
  if (c.returnsPercent < 0 || c.returnsPercent > 50) return 'Retourvoorziening: tussen 0 en 50%.'
  return null
}

/** Voorbeeldstromen per 30 dagen: aantal, gemiddeld bedrag incl. btw, brutomarge. */
const STREAMS = [
  { kind: 'salon', per30: 240, avgGross: 4800, margin: 39 },
  { kind: 'protocol', per30: 14, avgGross: 21000, margin: 41 },
  { kind: 'bijvullen', per30: 28, avgGross: 8200, margin: 40 },
  { kind: 'los', per30: 40, avgGross: 5200, margin: 38 },
] as const

function marginReport(from: string, to: string, costs: CostModel): MarginReport {
  const factor = Math.max(1, daysBetween(from, to) + 1) / 30
  const known = itemCosts()
  let freeShippingCents = 0
  let paymentCents = 0
  let giftCents = 0
  const streams: MarginStream[] = STREAMS.map((s) => {
    const orders = Math.round(s.per30 * factor)
    const net = Math.round((s.avgGross * 100) / 121)
    const cost = Math.round(net * (1 - s.margin / 100))
    let left: number
    let perOrderCosts: number
    if (s.kind === 'bijvullen') {
      left = boxContribution(s.avgGross, s.margin, costs)
      const gift = Math.round(costs.subscriptionGiftCents / Math.max(1, policy.subscription.giftEvery))
      left -= gift
      // Kosten per doos = brutomarge min wat er overblijft (incasso, pakket, verpakking, retour, cadeau).
      perOrderCosts = Math.round((net * s.margin) / 100) - left
      paymentCents += paymentCostCents(costs, 'incasso', s.avgGross) * orders
      freeShippingCents += (costs.shipping['pakket-nl'] + costs.packaging.pakket) * orders
      giftCents += gift * orders
    } else {
      const salon = s.kind === 'salon'
      const free = !salon && s.avgGross / 100 >= policy.freeShippingFrom
      const gift = s.kind === 'protocol' ? 800 : 0
      const r = orderContribution(
        {
          kind: s.kind,
          lines: [{ name: s.kind, qty: 1, grossCents: s.avgGross, vatRate: 21, costCents: cost }],
          paymentMethod: salon ? 'pin' : 'ideal',
          paidCents: s.avgGross,
          shipping: salon ? null : 'pakket-nl',
          giftCostCents: gift,
        },
        costs,
      )
      left = r.leftCents
      perOrderCosts = r.paymentCents + r.shippingCents + r.packagingCents + r.returnsCents + r.giftCents
      paymentCents += r.paymentCents * orders
      if (free) freeShippingCents += (costs.shipping['pakket-nl'] + packagingFor(costs, 'pakket-nl')) * orders
      giftCents += gift * orders
    }
    return {
      kind: s.kind,
      orders,
      revenueNetCents: net * orders,
      grossMarginPercent: s.margin,
      costsPerOrderCents: perOrderCosts,
      leftPerOrderCents: left,
      leftCents: left * orders,
    }
  }).sort((a, b) => b.leftCents - a.leftCents)

  const boxes = streams.find((s) => s.kind === 'bijvullen')?.orders ?? 0
  const failed = Math.round(boxes * 0.03)
  return {
    revenueNetCents: streams.reduce((n, s) => n + s.revenueNetCents, 0),
    leftCents: streams.reduce((n, s) => n + s.leftCents, 0) - failed * costs.payment.incasso.fixedCents,
    streams,
    freeShippingCents,
    paymentCents,
    giftCents,
    failedDebits: { count: failed, costCents: failed * costs.payment.incasso.fixedCents },
    parcelsSaved: Math.round(boxes * 0.35),
    missingCost: localCatalog.products.filter((p) => p.kind !== 'giftcard' && p.kind !== 'kuur' && known[p.id] === undefined).map((p) => ({ id: p.id, name: p.name })),
    estimated: true,
  }
}

export const demoCostsBackend: Pick<AdminBackend, 'fetchCosts' | 'saveCosts' | 'fetchInsightsMargin'> = {
  fetchCosts: () => reply(response(), 250),

  async saveCosts(req) {
    const stored = load()
    if (req.revision !== stored.revision) throw new ApiError('De kosten zijn intussen ergens anders opgeslagen.', 409, undefined, 'version_conflict')
    const problem = costsProblem(req.costs)
    if (problem) throw new ApiError(problem, 422)
    writeLocalJson(KEY, { revision: stored.revision + 1, costs: req.costs })
    return reply(response(), 400)
  },

  fetchInsightsMargin: (from, to) => reply(marginReport(from, to, load().costs), 350),
}
